import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, useEffect } from 'react'
import lazyWithRetry from './utils/lazyWithRetry'
import ChunkErrorBoundary from './components/ui/ChunkErrorBoundary'
import LandingLayout from './layouts/LandingLayout'
import AppLayout from './layouts/AppLayout'
import AdminLayout from './layouts/AdminLayout'
import UserLayout from './layouts/UserLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AdminRoute from './components/auth/AdminRoute'
import ToastContainer from './components/ui/Toast'
import CookieBanner from './components/ui/CookieBanner'
import { stopTracking, trackEvent } from './services/tracking'
import { usePageTracking } from './hooks/usePageTracking'
import { bufferUtm, captureUtm } from './services/utm'
import { applyConsent } from './services/consent'
import { useConsentStore } from './stores/useConsentStore'

const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'))
const AuthPage = lazyWithRetry(() => import('./pages/AuthPage'))
const MapPage = lazyWithRetry(() => import('./pages/MapPage'))
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'))
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'))
const SetPasswordPage = lazyWithRetry(() => import('./pages/SetPasswordPage'))
const ResendMigrationPage = lazyWithRetry(() => import('./pages/ResendMigrationPage'))
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'))
const LegalPage = lazyWithRetry(() => import('./pages/LegalPage'))
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'))
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'))
const GlossairePage = lazyWithRetry(() => import('./pages/GlossairePage'))
const GuidesPage = lazyWithRetry(() => import('./pages/GuidesPage'))
const GuideArticlePage = lazyWithRetry(() => import('./pages/GuideArticlePage'))
const CGUPage = lazyWithRetry(() => import('./pages/CGUPage'))
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'))

// Admin pages
const AdminDashboardPage = lazyWithRetry(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazyWithRetry(() => import('./pages/admin/AdminUsersPage'))
const AdminLogsPage = lazyWithRetry(() => import('./pages/admin/AdminLogsPage'))
const AdminAnalyticsPage = lazyWithRetry(() => import('./pages/admin/AdminAnalyticsPage'))
const AdminAcquisitionPage = lazyWithRetry(() => import('./pages/admin/AdminAcquisitionPage'))
const AdminWaitlistPage = lazyWithRetry(() => import('./pages/admin/AdminWaitlistPage'))

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-screen text-gray-500">
      Chargement...
    </div>
  )
}

export default function App() {
  useEffect(() => {
    // Sauvegarder les UTM en mémoire AVANT le consentement (pas de stockage persistant)
    bufferUtm()

    // Appliquer le consentement sauvegardé (active le tracking si accepté)
    const { analytique, marketing } = useConsentStore.getState()
    if (analytique !== null) {
      applyConsent({ analytique, marketing })
    }
    // Capturer les UTM si consentement déjà donné (visiteur de retour)
    const utmData = captureUtm()
    if (utmData) {
      trackEvent('landing.visit', utmData)
    }
    return () => stopTracking()
  }, [])

  usePageTracking()

  return (
    <>
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Landing / pages marketing */}
          <Route element={<LandingLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/mentions-legales" element={<LegalPage />} />
            <Route path="/confidentialite" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/glossaire" element={<GlossairePage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/guides/:slug" element={<GuideArticlePage />} />
            <Route path="/cgu" element={<CGUPage />} />
          </Route>

          {/* Authentification */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
          <Route path="/auth/resend-migration" element={<ResendMigrationPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Application carte (protégée) */}
          <Route element={<AppLayout />}>
            <Route path="/app" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
          </Route>

          {/* Pages app protegees (avec sidebar utilisateur) */}
          <Route element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
            <Route path="/account" element={<AccountPage />} />
          </Route>

          {/* Redirections sections désactivées */}
          <Route path="/dashboard" element={<Navigate to="/app" replace />} />
          <Route path="/projects/*" element={<Navigate to="/app" replace />} />
          <Route path="/projects" element={<Navigate to="/app" replace />} />

          {/* Backoffice admin */}
          <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/acquisition" element={<AdminAcquisitionPage />} />
            <Route path="/admin/waitlist" element={<AdminWaitlistPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
          </Route>

          {/* 404 */}
          <Route element={<LandingLayout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
      </ChunkErrorBoundary>
      <ToastContainer />
      <CookieBanner />
    </>
  )
}
